import { Button } from '@chiroup/components/Button';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { Textarea } from '@chiroup/components/form/Textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@chiroup/components/ui/tabs';
import {
  ClickBehavior,
  DEFAULT_TRIAL_HOME,
  mergeTrialHomeWithDefaults,
  TRIAL_CARD_ICON_OPTIONS,
  TRIAL_TOUR_OPTIONS,
  TrialCardIcon,
  TrialCardSection,
  TrialHomeCard,
  TrialHomeContent,
} from '@chiroup/core/types/TrialHome.type';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import appDataService from '../../services/app-data.service';
import Header from '../layout/Header';
import TrialHomePreview from './TrialHomePreview';

/**
 * Trial Home editor. Two-pane layout — editor on the left, live preview on
 * the right that re-renders as the admin types. Per-card editing uses tabs
 * (Content + On click). Save-status pill in the toolbar tracks whether the
 * in-memory `data` differs from the last persisted snapshot.
 *
 * Card order, section assignment, add/delete, and icon are all controlled
 * from this editor — the `cards` array is the source of truth at runtime.
 */
const TrialHomeEditor = () => {
  const [data, setData] = useState<TrialHomeContent>(DEFAULT_TRIAL_HOME);
  // Snapshot of the last successfully-saved data so we can detect "dirty"
  // state without a re-fetch. JSON.stringify is fine here — the data is small
  // and we only compare on render.
  const [savedSnapshot, setSavedSnapshot] = useState<string>(JSON.stringify(DEFAULT_TRIAL_HOME));
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(
    DEFAULT_TRIAL_HOME.cards[0]?.id ?? null
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  // Ref so we can scroll a selected card's editor panel into view when the
  // preview is clicked.
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    let isDestroyed = false;

    const loadData = async () => {
      try {
        const res = await appDataService.get('trialHome');

        if (!isDestroyed && res) {
          const merged = mergeTrialHomeWithDefaults(res);
          setData(merged);
          setSavedSnapshot(JSON.stringify(merged));
        }
      } catch {
        // Key doesn't exist yet — keep defaults.
      }
    };

    loadData();

    return () => {
      isDestroyed = true;
    };
  }, []);

  const isDirty = useMemo(() => JSON.stringify(data) !== savedSnapshot, [data, savedSnapshot]);

  const save = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      await appDataService.update('trialHome', data);
      setSavedSnapshot(JSON.stringify(data));
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch {
      setSaveMessage('Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = <K extends keyof TrialHomeContent>(field: K, value: TrialHomeContent[K]) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const updateCard = (id: string, patch: Partial<TrialHomeCard>) => {
    setData(prev => ({
      ...prev,
      cards: prev.cards.map(c => (c.id === id ? { ...c, ...patch } : c)),
    }));
  };

  /** Move a card up or down within its section. Card order across sections
   *  is irrelevant — sections render independently. */
  const reorderCard = (id: string, dir: 'up' | 'down') => {
    setData(prev => {
      const card = prev.cards.find(c => c.id === id);
      if (!card) return prev;
      const sectionCards = prev.cards.filter(c => c.section === card.section);
      const idx = sectionCards.findIndex(c => c.id === id);
      const swapWith = sectionCards[dir === 'up' ? idx - 1 : idx + 1];
      if (!swapWith) return prev;

      const aIdx = prev.cards.findIndex(c => c.id === card.id);
      const bIdx = prev.cards.findIndex(c => c.id === swapWith.id);
      const next = [...prev.cards];
      [next[aIdx], next[bIdx]] = [next[bIdx], next[aIdx]];

      return { ...prev, cards: next };
    });
  };

  const addCard = (section: TrialCardSection) => {
    const id = `custom-${Math.random().toString(36).slice(2, 8)}`;
    const newCard: TrialHomeCard = {
      id,
      enabled: true,
      section,
      icon: 'sparkles',
      title: 'New card',
      description: 'Describe this card.',
      clickBehavior: { kind: 'none' },
    };
    setData(prev => ({ ...prev, cards: [...prev.cards, newCard] }));
    setSelectedCardId(id);
    requestAnimationFrame(() => {
      cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const removeCard = (id: string) => {
    setData(prev => ({ ...prev, cards: prev.cards.filter(c => c.id !== id) }));
    setSelectedCardId(prev => (prev === id ? null : prev));
  };

  const onSelectCardFromPreview = (id: string) => {
    setSelectedCardId(id);
    // Close the preview modal so the user lands directly on the card's
    // editor (which we'll smooth-scroll into view).
    setPreviewOpen(false);
    requestAnimationFrame(() => {
      cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const essentialsCards = data.cards.filter(c => c.section === 'essentials');
  const ehrCards = data.cards.filter(c => c.section === 'ehr');

  return (
    <>
      <Header
        title="Trial Home"
        rightSide={
          <div className="flex items-center gap-3">
            <DirtyStatePill isDirty={isDirty} saveMessage={saveMessage} />
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-sm font-medium text-gray-700 dark:text-dark-gray-200 transition-colors"
            >
              <EyeIcon className="h-4 w-4" />
              Preview
            </button>
            <Button text={isDirty ? 'Save changes' : 'Saved'} onClick={save} loading={isSaving} />
          </div>
        }
      />
      <div className="flex flex-col gap-4 m-4">
        <div className="w-full flex flex-col gap-4 min-w-0">
          <p className="text-sm text-gray-500 dark:text-dark-gray-400">
            Shown to users on the trial plan after they log in. Click{' '}
            <span className="font-medium text-gray-700 dark:text-dark-gray-200">Preview</span> at
            the top to see what your changes look like.
          </p>

          {/* Page-level fields */}
          <Panel title="Page">
            <Input
              name="heading"
              label="Page heading"
              value={data.heading}
              onChange={val => updateField('heading', val)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <Input
                name="essentialsLabel"
                label="Essentials section label"
                value={data.essentialsLabel}
                onChange={val => updateField('essentialsLabel', val)}
              />
              <Input
                name="ehrLabel"
                label="EHR section label"
                value={data.ehrLabel}
                onChange={val => updateField('ehrLabel', val)}
              />
            </div>
          </Panel>

          {/* Cards — Essentials */}
          <CardSection
            label="Essentials cards"
            section="essentials"
            cards={essentialsCards}
            onAdd={() => addCard('essentials')}
            renderCard={(card, idx, total) => (
              <div
                key={card.id}
                ref={el => {
                  cardRefs.current[card.id] = el;
                }}
              >
                <CardEditorPanel
                  card={card}
                  update={patch => updateCard(card.id, patch)}
                  isSelected={selectedCardId === card.id}
                  onSelect={() => setSelectedCardId(card.id)}
                  onMoveUp={idx > 0 ? () => reorderCard(card.id, 'up') : undefined}
                  onMoveDown={idx < total - 1 ? () => reorderCard(card.id, 'down') : undefined}
                  onDelete={() => removeCard(card.id)}
                />
              </div>
            )}
          />

          {/* Cards — EHR */}
          <CardSection
            label="EHR cards"
            section="ehr"
            cards={ehrCards}
            onAdd={() => addCard('ehr')}
            className="mt-2"
            renderCard={(card, idx, total) => (
              <div
                key={card.id}
                ref={el => {
                  cardRefs.current[card.id] = el;
                }}
              >
                <CardEditorPanel
                  card={card}
                  update={patch => updateCard(card.id, patch)}
                  isSelected={selectedCardId === card.id}
                  onSelect={() => setSelectedCardId(card.id)}
                  onMoveUp={idx > 0 ? () => reorderCard(card.id, 'up') : undefined}
                  onMoveDown={idx < total - 1 ? () => reorderCard(card.id, 'down') : undefined}
                  onDelete={() => removeCard(card.id)}
                />
              </div>
            )}
          />

          {/* CTA */}
          <SectionLabel className="mt-2">Bottom CTA</SectionLabel>
          <Panel>
            <Input
              name="ctaTitle"
              label="Title"
              value={data.ctaTitle}
              onChange={val => updateField('ctaTitle', val)}
            />
            <Input
              name="ctaButton"
              label="Button text"
              value={data.ctaButton}
              onChange={val => updateField('ctaButton', val)}
              className="mt-3"
            />
            <Textarea
              name="ctaSubtitle"
              label="Subtitle (small fine-print under the button)"
              value={data.ctaSubtitle || ''}
              onChange={val => updateField('ctaSubtitle', val)}
              rows={2}
              className="mt-3"
            />
          </Panel>
        </div>
      </div>

      <PreviewModal
        open={previewOpen}
        data={data}
        selectedCardId={selectedCardId}
        onSelectCard={onSelectCardFromPreview}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
};

const PreviewModal: React.FC<{
  open: boolean;
  data: TrialHomeContent;
  selectedCardId: string | null;
  onSelectCard: (id: string) => void;
  onClose: () => void;
}> = ({ open, data, selectedCardId, onSelectCard, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Trial Home preview"
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] bg-white dark:bg-dark-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-dark-gray-700 bg-gray-50 dark:bg-dark-gray-800">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-300/80" />
            <span className="ml-3 text-[11px] font-mono uppercase tracking-wider text-gray-500">
              Trial home — preview
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-dark-gray-700 transition-colors"
            aria-label="Close preview"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <TrialHomePreview
            data={data}
            selectedCardId={selectedCardId}
            onSelectCard={onSelectCard}
          />
        </div>
        <div className="px-5 py-2.5 border-t border-gray-100 dark:border-dark-gray-700 bg-gray-50 dark:bg-dark-gray-800 text-[11px] text-gray-500 dark:text-dark-gray-400">
          Click any card to jump to its editor.
        </div>
      </div>
    </div>,
    document.body
  );
};

const DirtyStatePill: React.FC<{ isDirty: boolean; saveMessage: string }> = ({
  isDirty,
  saveMessage,
}) => {
  if (saveMessage === 'Saved!') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
        <CheckCircleIcon className="h-4 w-4" />
        Saved
      </span>
    );
  }

  if (saveMessage === 'Error saving') {
    return <span className="text-xs font-medium text-red-600">Error saving</span>;
  }

  if (isDirty) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Unsaved changes
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
      Up to date
    </span>
  );
};

const Panel: React.FC<{
  title?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className }) => (
  <div
    className={[
      'bg-white dark:bg-dark-gray-800 border border-gray-200 dark:border-dark-gray-700 rounded-lg p-4',
      className || '',
    ].join(' ')}
  >
    {title && (
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
        {title}
      </h3>
    )}
    {children}
  </div>
);

const SectionLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <h3
    className={[
      'text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 px-1',
      className || '',
    ].join(' ')}
  >
    {children}
  </h3>
);

const SECTION_OPTIONS = [
  { text: 'Essentials', value: 'essentials' },
  { text: 'EHR', value: 'ehr' },
];

const CardSection: React.FC<{
  label: string;
  section: TrialCardSection;
  cards: TrialHomeCard[];
  onAdd: () => void;
  renderCard: (card: TrialHomeCard, index: number, total: number) => React.ReactNode;
  className?: string;
}> = ({ label, cards, onAdd, renderCard, className }) => (
  <div className={['flex flex-col gap-3', className || ''].join(' ')}>
    <div className="flex items-center justify-between">
      <SectionLabel>{label}</SectionLabel>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-xs font-medium text-gray-700 dark:text-dark-gray-200 transition-colors"
      >
        <PlusIcon className="h-3.5 w-3.5" />
        Add card
      </button>
    </div>
    {cards.length === 0 && (
      <div className="text-xs italic text-gray-400 dark:text-dark-gray-500 px-4 py-3 border border-dashed border-gray-200 dark:border-dark-gray-700 rounded-lg">
        No cards in this section yet. Click <span className="font-semibold">Add card</span> above.
      </div>
    )}
    {cards.map((c, i) => renderCard(c, i, cards.length))}
  </div>
);

const CardEditorPanel: React.FC<{
  card: TrialHomeCard;
  update: (patch: Partial<TrialHomeCard>) => void;
  isSelected: boolean;
  onSelect: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDelete: () => void;
}> = ({ card, update, isSelected, onSelect, onMoveUp, onMoveDown, onDelete }) => (
  <div
    onClick={onSelect}
    className={[
      'bg-white dark:bg-dark-gray-800 border rounded-lg p-4 transition-shadow',
      isSelected
        ? 'border-primary-300 dark:border-primary-700 ring-2 ring-primary-200/60 dark:ring-primary-900/40 shadow-sm'
        : 'border-gray-200 dark:border-dark-gray-700',
    ].join(' ')}
  >
    <div className="flex items-center justify-between mb-3 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs font-mono text-gray-400 uppercase tracking-wide truncate">
          {card.id}
        </span>
        {!card.enabled && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 dark:bg-dark-gray-700 dark:text-dark-gray-400">
            Hidden
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <IconButton
          icon={<ArrowUpIcon className="h-3.5 w-3.5" />}
          label="Move up"
          onClick={onMoveUp}
          disabled={!onMoveUp}
        />
        <IconButton
          icon={<ArrowDownIcon className="h-3.5 w-3.5" />}
          label="Move down"
          onClick={onMoveDown}
          disabled={!onMoveDown}
        />
        <IconButton
          icon={<TrashIcon className="h-3.5 w-3.5" />}
          label="Delete card"
          onClick={() => {
            if (window.confirm(`Delete "${card.title || card.id}"?`)) onDelete();
          }}
          tone="danger"
        />
        <Checkbox
          name={`enabled-${card.id}`}
          label="Show"
          value={card.enabled}
          onChange={val => update({ enabled: val })}
        />
      </div>
    </div>

    <Tabs defaultValue="content" className="w-full">
      <TabsList className="grid grid-cols-2 w-full">
        <TabsTrigger value="content">Content</TabsTrigger>
        <TabsTrigger value="behavior">On click</TabsTrigger>
      </TabsList>
      <TabsContent value="content" className="pt-3 flex flex-col gap-3">
        <Input
          name={`title-${card.id}`}
          label="Title"
          value={card.title}
          onChange={val => update({ title: val })}
        />
        <Textarea
          name={`description-${card.id}`}
          label="Description"
          value={card.description}
          onChange={val => update({ description: val })}
          rows={2}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            name={`badge-${card.id}`}
            label="Badge (optional, e.g., Start here)"
            value={card.badge || ''}
            onChange={val => update({ badge: val })}
          />
          <Select
            name={`icon-${card.id}`}
            label="Icon"
            value={card.icon}
            options={TRIAL_CARD_ICON_OPTIONS.map(o => ({ text: o.label, value: o.id }))}
            onChange={(v: string) => update({ icon: v as TrialCardIcon })}
            alwaysReturnArray={false}
          />
        </div>
        <Select
          name={`section-${card.id}`}
          label="Section"
          value={card.section}
          options={SECTION_OPTIONS}
          onChange={(v: string) => update({ section: v as TrialCardSection })}
          alwaysReturnArray={false}
        />
      </TabsContent>
      <TabsContent value="behavior" className="pt-3">
        <ClickBehaviorEditor
          cardId={card.id}
          value={card.clickBehavior}
          onChange={cb => update({ clickBehavior: cb })}
        />
      </TabsContent>
    </Tabs>
  </div>
);

const IconButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}> = ({ icon, label, onClick, disabled, tone = 'default' }) => (
  <button
    type="button"
    onClick={e => {
      e.stopPropagation();
      onClick?.();
    }}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={[
      'p-1.5 rounded-md border transition-colors disabled:opacity-30 disabled:cursor-not-allowed',
      tone === 'danger'
        ? 'border-gray-300 dark:border-dark-gray-600 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400'
        : 'border-gray-300 dark:border-dark-gray-600 text-gray-500 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-dark-gray-700 dark:hover:text-dark-gray-100',
    ].join(' ')}
  >
    {icon}
  </button>
);

const CLICK_KIND_OPTIONS = [
  { text: 'Start an in-app tour', value: 'tour' },
  { text: 'Play a video (YouTube / Loom)', value: 'video' },
  { text: 'Navigate to a route', value: 'route' },
  { text: 'No action (informational)', value: 'none' },
];

const ClickBehaviorEditor: React.FC<{
  cardId: string;
  value?: ClickBehavior;
  onChange: (cb: ClickBehavior) => void;
}> = ({ cardId, value, onChange }) => {
  const kind = value?.kind ?? 'none';

  const setKind = (next: string) => {
    switch (next) {
      case 'tour':
        onChange({ kind: 'tour', tourId: TRIAL_TOUR_OPTIONS[0]?.id ?? '' });
        break;
      case 'video':
        onChange({ kind: 'video', url: '' });
        break;
      case 'route':
        onChange({ kind: 'route', path: '' });
        break;
      case 'none':
      default:
        onChange({ kind: 'none' });
        break;
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <Select
        name={`click-kind-${cardId}`}
        label="Behavior"
        value={kind}
        options={CLICK_KIND_OPTIONS}
        onChange={(v: string) => setKind(v)}
        alwaysReturnArray={false}
      />
      {value?.kind === 'tour' && (
        <Select
          name={`click-tour-${cardId}`}
          label="Tour"
          value={value.tourId}
          options={TRIAL_TOUR_OPTIONS.map(t => ({ text: t.label, value: t.id }))}
          onChange={(v: string) => onChange({ kind: 'tour', tourId: v })}
          alwaysReturnArray={false}
        />
      )}
      {value?.kind === 'video' && (
        <>
          <Input
            name={`click-video-url-${cardId}`}
            label="Video URL (YouTube share link, Loom share link, or any embed URL)"
            value={value.url}
            onChange={(v: string) => onChange({ kind: 'video', url: v, title: value.title })}
          />
          <Input
            name={`click-video-title-${cardId}`}
            label="Video title (shown in the modal header, optional)"
            value={value.title || ''}
            onChange={(v: string) => onChange({ kind: 'video', url: value.url, title: v })}
          />
        </>
      )}
      {value?.kind === 'route' && (
        <Input
          name={`click-route-${cardId}`}
          label="Path (e.g., /patients, /schedule, /billing)"
          value={value.path}
          onChange={(v: string) => onChange({ kind: 'route', path: v })}
        />
      )}
    </div>
  );
};

export default TrialHomeEditor;
